import {
	AlertDialog,
	AlertDialogBody,
	AlertDialogContent,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogOverlay,
	Box,
	Button,
	HStack,
	List,
	ListItem,
	useDisclosure,
} from "@chakra-ui/react";
import { Panel, PanelBody } from "@wordpress/components";
import { sprintf, __ } from "@wordpress/i18n";
import React from "react";
import { PopoverDrawer } from "../../controls";
import Input from "../../controls/input/Input";
import { RawTypography } from "../../controls/typography/Typography";
import useGlobalStyles from "../../hooks/useGlobalStyles";
import { DEFAULT_IDS } from "./constants";

export const Typographies = () => {
	const { styles, updateStyleByKey, removeStyleByKey } = useGlobalStyles();
	const [deleteId, setDeleteId] = React.useState("");
	const { isOpen, onOpen, onClose } = useDisclosure();
	const cancelRef = React.useRef<HTMLButtonElement>(null);

	return (
		<Panel>
			<Box
				as={PanelBody}
				initialOpen={false}
				title={__("Typography")}
				mb="1px"
			>
				<List>
					{styles.typographies
						.filter((t) => DEFAULT_IDS.includes(t.id))
						.map((t) => (
							<ListItem
								key={t.id}
								sx={{
									".chakra-form-control": {
										py: "12px",
									},
								}}
								mb="0"
							>
								<PopoverDrawer
									label={t.name}
									closeOnFocusOutside
									popoverDivProps={{
										sx: {
											".components-popover__content": {
												minW: "254px",
												p: 4,
											},
										},
									}}
									popoverProps={{
										offset: 16,
									}}
								>
									<RawTypography
										value={t.value}
										onChange={(v) => {
											updateStyleByKey("typographies", {
												...t,
												value: v,
											});
										}}
									/>
								</PopoverDrawer>
							</ListItem>
						))}
					<ListItem
						display="flex"
						alignItems="center"
						justifyContent="space-between"
						py="12px"
						mb="0"
					>
						Custom Typography{" "}
						<Button
							variant="icon"
							onClick={() =>
								updateStyleByKey("typographies", {
									name: "",
									id: "",
									value: {},
								})
							}
						>
							<svg
								stroke="currentColor"
								fill="currentColor"
								strokeWidth="0"
								viewBox="0 0 448 512"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path d="M256 80c0-17.7-14.3-32-32-32s-32 14.3-32 32V224H48c-17.7 0-32 14.3-32 32s14.3 32 32 32H192V432c0 17.7 14.3 32 32 32s32-14.3 32-32V288H400c17.7 0 32-14.3 32-32s-14.3-32-32-32H256V80z"></path>
							</svg>
						</Button>
					</ListItem>
					{styles.typographies
						.filter((t) => !DEFAULT_IDS.includes(t.id))
						.map((t) => (
							<ListItem
								key={t.id}
								mb="0"
								sx={{
									"> .chakra-form-control": {
										py: "12px",
									},
								}}
							>
								<PopoverDrawer
									label={() => (
										<Input
											label={t.name}
											value={t.name}
											onChange={(v) =>
												updateStyleByKey(
													"typographies",
													{
														...t,
														name: v,
													}
												)
											}
											hiddenLabel
											border="none"
											borderColor="var(--chakra-colors-primary-500)!important"
											_focus={{
												border: "1px",
												borderColor:
													"var(--chakra-colors-primary-500)!important",
												outline: "none !important",
												boxShadow: "none !important",
												px: 2,
											}}
											h="28px"
											minH="28px !important"
											px="0"
										/>
									)}
									trigger={(triggerProps) => (
										<HStack>
											<Button
												onClick={() => {
													setDeleteId(t.id);
													onOpen();
												}}
												variant="icon"
												sx={{
													svg: {
														color: "gray.400",
														w: "14px",
														h: "14px",
													},
												}}
											>
												<svg
													stroke="currentColor"
													fill="none"
													strokeWidth="2"
													viewBox="0 0 24 24"
													aria-hidden="true"
													xmlns="http://www.w3.org/2000/svg"
												>
													<path
														strokeLinecap="round"
														strokeLinejoin="round"
														d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
													></path>
												</svg>
											</Button>
											<PopoverDrawer.Trigger
												{...triggerProps}
											/>
										</HStack>
									)}
									closeOnFocusOutside
									popoverDivProps={{
										sx: {
											".components-popover__content": {
												minW: "254px",
												p: 4,
											},
										},
									}}
									popoverProps={{
										offset: 16,
									}}
								>
									<RawTypography
										value={t.value}
										onChange={(v) => {
											updateStyleByKey("typographies", {
												...t,
												value: v,
											});
										}}
									/>
								</PopoverDrawer>
							</ListItem>
						))}
				</List>
			</Box>
			<AlertDialog
				isOpen={isOpen}
				leastDestructiveRef={cancelRef}
				onClose={() => {
					onClose();
					setDeleteId("");
				}}
			>
				<AlertDialogOverlay>
					<AlertDialogContent>
						<AlertDialogHeader fontSize="lg" fontWeight="bold">
							{sprintf(
								__("Delete typography #%s", "magazine-blocks"),
								deleteId
							)}
						</AlertDialogHeader>

						<AlertDialogBody>
							{__(
								"Are you sure? You cannot undo this action afterwards.",
								"magazine-blocks"
							)}
						</AlertDialogBody>

						<AlertDialogFooter>
							<Button
								ref={cancelRef}
								variant="outline"
								size="sm"
								onClick={() => {
									onClose();
									setDeleteId("");
								}}
							>
								{__("Cancel", "magazine-blocks")}
							</Button>
							<Button
								size="sm"
								onClick={() => {
									onClose();
									removeStyleByKey("typographies", deleteId);
								}}
								ml={3}
							>
								{__("Delete", "magazine-blocks")}
							</Button>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialogOverlay>
			</AlertDialog>
		</Panel>
	);
};

export default Typographies;
